import { EditorFeedbackReport } from 'src/hooks/useEditorFeedback';

/**
 * Static sample editorial report, shown on the public sample page so authors
 * can see the full deliverable before running (or paying for) their own. Uses
 * a public-domain work (Pride and Prejudice) so it can ship in the bundle.
 *
 * Served from the app bundle — zero marginal cost per view. Regenerate from a
 * real run's editorFeedback.report if you want it to track the live pipeline
 * output exactly.
 */
export const SAMPLE_REPORT_BOOK_TITLE = 'Pride and Prejudice';

export const sampleEditorialReport: EditorFeedbackReport = {
  summary:
    'A structurally assured social comedy whose engine is a single, sustained irony: the narrator tells us in the first line what the characters spend fifty chapters learning. The book carries itself on voice and on the Elizabeth–Darcy misreading, which is dramatized (letters, overheard remarks, revisited scenes) rather than narrated. The middle sags slightly around the Netherfield departure, and the Lydia subplot leans on coincidence to resolve, but both are covered by pace and character. This is a manuscript to tighten, not to rebuild.',
  fixFirst: [
    {
      n: 1,
      text: 'Bridge the mid-book lull after Bingley leaves Netherfield — the momentum drops before Darcy’s first proposal. Give Elizabeth an active want in these chapters, not just a reactive one.',
    },
    {
      n: 2,
      text: 'Darcy’s off-page intervention to arrange Lydia’s marriage is the hinge of the ending but happens where the reader can’t see it. Consider one dramatized beat so the resolution feels earned rather than reported.',
    },
    {
      n: 3,
      text: 'Mr. Bennet’s culpability is raised (his failure to check Lydia) then dropped. Either pay it off or trim the setup so it doesn’t read as an unkept promise.',
    },
  ],
  plotHoles: [
    {
      rank: 1,
      title:
        'Lydia’s rescue turns on Darcy’s concealed, unmotivated omniscience',
      severity: 'major',
      detail:
        'Darcy locates Wickham and Lydia in London, negotiates the marriage, and pays Wickham’s debts entirely off-page. The reader learns it secondhand through Mrs. Gardiner’s letter. Because we never see Darcy decide or act, the climax of his redemption arc is asserted rather than shown, and his knowledge of Wickham’s whereabouts is convenient.',
      quotes: [
        {
          ch: 'Ch. 52',
          text: '“…it was owing to him, to his reserve and want of proper consideration, that Wickham’s character had been so misunderstood.”',
        },
      ],
    },
    {
      rank: 2,
      title: 'The timeline of Jane’s London stay strains credulity',
      severity: 'minor',
      detail:
        'Jane spends roughly three months in London a few miles from Bingley, who never calls, on the strength of Miss Bingley’s interference alone. For a man later shown to be genuinely attached, the total absence of contact over that span is thinly motivated.',
      quotes: [
        {
          ch: 'Ch. 26',
          text: '“…she had been in town three months, and Bingley had never seen her.”',
        },
      ],
    },
    {
      rank: 3,
      title: 'Mr. Bennet’s stated resolve to reform is never tested',
      severity: 'minor',
      detail:
        'After Lydia’s elopement, Mr. Bennet accepts blame and implies a change. The narrative closes his arc with a joke instead of a consequence, leaving the earlier self-reproach unresolved.',
      quotes: [
        {
          ch: 'Ch. 48',
          text: '“let me once in my life feel how much I have been to blame.”',
        },
      ],
    },
  ],
  characters: [
    {
      role: 'Principals',
      members: [
        {
          name: 'Elizabeth Bennet',
          cites: 'Ch. 1–61',
          traits: 'Quick, ironic, proud of her own discernment.',
          motivation:
            'To marry only for genuine regard — and to be proven right in her judgments.',
          arc: 'The strongest arc in the book: her certainty is systematically dismantled (Wickham, the letter, Pemberley) until pride and prejudice are shown to be her own, not only Darcy’s. Fully paid off.',
        },
        {
          name: 'Fitzwilliam Darcy',
          cites: 'Ch. 3–61',
          traits: 'Reserved, exacting, socially graceless.',
          motivation:
            'To act rightly by his own standards, gradually redefined by Elizabeth’s judgment of him.',
          arc: 'A genuine change, but its pivotal action (the Lydia affair) happens off-page — see plot hole #1. The letter in Ch. 35 does most of the visible work.',
        },
      ],
    },
    {
      role: 'Family',
      members: [
        {
          name: 'Jane Bennet',
          cites: 'Ch. 1–61',
          traits: 'Gentle, credulous of others’ goodness.',
          motivation:
            'To think well of everyone — a foil to Elizabeth’s suspicion.',
          arc: 'A flat (stable) arc by design; she is the control against which Elizabeth’s change is measured.',
        },
        {
          name: 'Mr. Bennet',
          cites: 'Ch. 1–61',
          traits: 'Detached, witty, negligent.',
          motivation: 'To be amused and left alone.',
          arc: 'Setup for a reckoning after Lydia’s flight that the book declines to deliver — see fix-first #3.',
        },
      ],
    },
  ],
  acts: [
    {
      name: 'Act I — The field of play',
      chapters: 'Ch. 1–23',
      note: 'Establishes the marriage market, the Bennet household, and the two misreadings (Wickham’s charm, Darcy’s pride). Ends on Collins’s proposal and Charlotte’s pragmatic acceptance — the book’s thesis stated in the negative.',
    },
    {
      name: 'Act II — Misjudgment and correction',
      chapters: 'Ch. 24–42',
      note: 'The engine room. Darcy’s proposal and letter invert everything the reader and Elizabeth believed. The mid-act lull around the Netherfield departure is the manuscript’s softest stretch — see fix-first #1.',
    },
    {
      name: 'Act III — Recognition and reward',
      chapters: 'Ch. 43–61',
      note: 'Pemberley recontextualizes Darcy; the Lydia crisis forces both characters to act. Resolution is swift and satisfying, though it leans on the off-page rescue.',
    },
  ],
  unusedSetups: [
    {
      ch: 'Ch. 6',
      title: 'Charlotte’s theory of marriage',
      note: 'Charlotte’s cold-eyed thesis that “happiness in marriage is entirely a matter of chance” is planted early and embodied in her own match, but never directly re-confronted by Elizabeth’s ending. A deliberate silence — but a payoff opportunity.',
    },
    {
      ch: 'Ch. 25',
      title: 'The Gardiners’ planned tour to the Lakes',
      note: 'Introduced as a Lake District trip, then curtailed to Derbyshire for plot convenience (Pemberley). The original framing is dropped rather than used.',
    },
  ],
  continuityNotes: [
    {
      ch: 'Ch. 30',
      text: 'Elizabeth’s stated age (“not one-and-twenty”) sits slightly awkwardly against the implied span of events across the seasons.',
    },
    {
      ch: 'Ch. 55',
      text: 'The interval between Bingley’s return and his proposal compresses noticeably relative to the earlier, more leisurely Netherfield chapters.',
    },
  ],
};
